import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Check, X, Circle } from 'lucide-react';
import { useLessonsProgress } from '../hooks/useLessonsProgress';
import { LessonId } from '../services/lessonsService';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useConfirmDialog } from '../../../contexts/ConfirmDialogContext';

interface LessonItem {
  id: LessonId;
  title: string;
  description: string;
  available: boolean;
}

const LESSONS: LessonItem[] = [
  {
    id: 'lesson-1',
    title: 'Clear your head',
    description: 'Turn a messy thought into actionable tasks',
    available: true,
  },
  {
    id: 'lesson-2',
    title: 'Draft today',
    description: 'Let Orlo build a starting schedule',
    available: true,
  },
  {
    id: 'lesson-3',
    title: 'Move a goal forward',
    description: 'Break a goal into your first step',
    available: true,
  },
];

/**
 * LessonsPanel
 *
 * Today 頁右下角的 Onboarding Lessons 清單。
 * 使用 Portal + fixed 定位，不影響頁面結構。
 * 所有 Lessons 完成後自動隱藏。
 */
export function LessonsPanel() {
  const navigate = useNavigate();
  const { getLessonStatus, skipAllRemaining } = useLessonsProgress();
  const { setLessonsPanelVisible } = useOnboarding();
  const { showConfirm } = useConfirmDialog();
  const [minimized, setMinimized] = useState(false);

  // 通知 OnboardingContext LessonsPanel 可見，使一般 Tip 暫停顯示
  useEffect(() => {
    setLessonsPanelVisible(true);
    return () => setLessonsPanelVisible(false);
  }, [setLessonsPanelVisible]);

  // 「已解決」＝ completed 或 skipped，跳過視為與完成同等的結束狀態
  const isResolved = (lessonId: LessonId) => getLessonStatus(lessonId) !== 'not_started';

  // 所有「已開放」的 lesson 皆已解決（完成或跳過）後隱藏
  // （未開放的 lesson 上線後會讓 Panel 重新出現）
  const isAllAvailableResolved = LESSONS
    .filter((l) => l.available)
    .every((l) => isResolved(l.id));

  if (isAllAvailableResolved) {
    return null;
  }

  // 前置 lesson 只要已解決（completed 或 skipped）即視為通過，
  // 跳過某堂課不會卡死後續課程的解鎖
  const isPrerequisitesMet = (lesson: LessonItem): boolean => {
    const index = LESSONS.indexOf(lesson);
    return LESSONS.slice(0, index)
      .filter((l) => l.available)
      .every((l) => isResolved(l.id));
  };

  const handleLessonClick = (lesson: LessonItem) => {
    if (!lesson.available || isResolved(lesson.id)) return;
    if (!isPrerequisitesMet(lesson)) return;
    const route = lesson.id === 'lesson-2' ? '/calendar'
      : lesson.id === 'lesson-3' ? '/goals'
      : '/captures';
    navigate(route, { state: { lessonId: lesson.id } });
  };

  const handleSkipAll = async () => {
    const shouldSkip = await showConfirm({
      type: 'warning',
      title: 'Skip remaining lessons?',
      message: 'All unfinished lessons will be skipped for good. Completed lessons are not affected.',
      confirmText: 'Skip remaining',
      cancelText: 'Cancel',
      cancelIsPrimary: true,
    });
    if (shouldSkip) {
      skipAllRemaining();
    }
  };

  // 第一個尚未解決且可用的 lesson = 當前進行中
  const currentLessonId = LESSONS.find(
    (l) => l.available && !isResolved(l.id)
  )?.id ?? null;

  const panel = (
    <div
      className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-4 sm:w-72 rounded-2xl bg-white border border-gray-200 shadow-lg z-floating overflow-hidden"
      role="complementary"
      aria-label="Onboarding lessons"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-700">
        <div>
          <p className="text-sm font-semibold text-white">Get started</p>
          <p className="text-xs text-slate-300">
            {LESSONS.filter((l) => getLessonStatus(l.id) === 'completed').length} / {LESSONS.length} done
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <button
            onClick={handleSkipAll}
            className="text-xs text-slate-300 hover:text-white transition-colors"
          >
            Skip
          </button>
          <button
            onClick={() => setMinimized((prev) => !prev)}
            aria-label={minimized ? 'Expand lessons' : 'Minimize lessons'}
            className="p-1 rounded-md text-slate-300 hover:text-white hover:bg-slate-600 transition-colors"
          >
            {minimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Lesson list */}
      {!minimized && (
        <ul className="p-2 space-y-1">
          {LESSONS.map((lesson) => {
            const status = getLessonStatus(lesson.id);
            const resolved = isResolved(lesson.id);
            const clickable = lesson.available && !resolved && isPrerequisitesMet(lesson);
            const isCurrent = lesson.id === currentLessonId;
            const statusLabel = status === 'completed' ? ' (completed)' : status === 'skipped' ? ' (skipped)' : '';

            return (
              <li key={lesson.id}>
                <button
                  onClick={() => handleLessonClick(lesson)}
                  disabled={!clickable}
                  className={[
                    'w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors',
                    clickable ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default opacity-50',
                  ].join(' ')}
                  aria-label={`${lesson.title}: ${lesson.description}${statusLabel}`}
                >
                  {/* 狀態圖示：completed 藍色勾選 / skipped 灰色叉號 / not_started 圓點 */}
                  <span className="mt-0.5 shrink-0 relative">
                    {status === 'completed' ? (
                      <Check size={16} className="text-sky-600" />
                    ) : status === 'skipped' ? (
                      <X size={16} className="text-gray-400" />
                    ) : isCurrent ? (
                      <>
                        {/* ping 擴散層 */}
                        <span className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-50" />
                        <Circle size={16} className="relative text-sky-500" />
                      </>
                    ) : (
                      <Circle size={16} className="text-gray-300" />
                    )}
                  </span>

                  {/* 內容 */}
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        resolved
                          ? 'line-through text-gray-400'
                          : isCurrent
                            ? 'text-sky-700'
                            : 'text-gray-900'
                      }`}
                    >
                      {lesson.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{lesson.description}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}
